/**
 * Component displaying the usage for the billing page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */
import { logger } from '@/config/logger'
import {
  AccountUtil,
  LogTag,
  TextUtil,
  TierType,
  Util,
  writeCsv,
} from '@amplify3/duplo-back-end'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { ReactElement, useEffect, useState } from 'react'
import { BiChip, BiCoin, BiCoinStack } from 'react-icons/bi'
import { PiMusicNotes } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import Button from '../../components/Button'
import './billing.css'
import { Kpi, PlanInvoice, TaskDetail } from './Invoice'
import './UsageTab.css'

const DefaultPeriodExportButtonLabel = 'Selected period'
const CSV_FILE_NAME_PREFIX = 'usage'
const CSV_FILE_EXT = '.csv'

/**
 * A column header definition with an icon and a title.
 */
interface ColumnHeader {
  icon: ReactElement
  title: string
}

/**
 * The list of column headers of the task table.
 */
const HeaderList: ColumnHeader[] = [
  {
    icon: <BiChip />,
    title: 'Product',
  },
  {
    icon: <PiMusicNotes />,
    title: 'Tracks processed',
  },
  {
    icon: <BiCoin />,
    title: 'Credits',
  },
  {
    icon: <BiCoinStack />,
    title: 'Additional credits',
    // span: 2,
  },
]

interface TaskTableProps {
  taskDetailList: Record<string, TaskDetail>
}

/**
 * The table showing the usage data of the selected month, per product.
 */
const TaskTable = (props: TaskTableProps) => {
  const navigate = useNavigate()
  const HeaderIconTitle = (headerProps: ColumnHeader) => {
    return (
      <th>
        <div className="header-icon-title">
          {headerProps.icon}
          {headerProps.title}
        </div>
      </th>
    )
  }

  return (
    <table className="task-table">
      <thead>
        <tr>
          {HeaderList.map((header: ColumnHeader) => (
            <HeaderIconTitle key={header.title} {...header} />
          ))}
        </tr>
      </thead>
      <tbody>
        {Object.values(props.taskDetailList)
          .sort((a, b) => a.moduleName.localeCompare(b.moduleName))
          .map((detail, index) => (
            <tr key={index}>
              <td>{detail.moduleName}</td>
              <td>{detail.trackProcessedCount}</td>
              <td>{Util.absRounding(detail.consumedCreditCount)}</td>
              <td className="additional-credit-column">
                <div className="additional-credit">
                  {Util.absRounding(detail.consumedOffPackageCreditCount)}
                </div>
                {detail.consumedOffPackageCreditCount > 0 && (
                  <button
                    type="button"
                    className="additional-credit-button"
                    onClick={() => navigate('/tierselection')}
                  >
                    Upgrade
                  </button>
                )}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  )
}

export interface UsageTableProps {
  className?: string
  invoice?: PlanInvoice
}

/**
 * The Usage tab component.
 */
const UsageTab = (props: UsageTableProps) => {
  const fullClassName = `${props.className} usage-table`
  const [selectedKpiIndex, setSelectedKpiIndex] = useState<number | undefined>(
    props.invoice?.kpiHistoryList && props.invoice?.kpiHistoryList.length > 0
      ? 0
      : undefined
  )
  const [selectedKpi, setSelectedKpi] = useState<Kpi | undefined>(
    props.invoice?.kpiHistoryList[0] ?? undefined
  )
  const [periodExportButtonLabel, setPeriodExportButtonLabel] =
    useState<string>(DefaultPeriodExportButtonLabel)

  const isKpiHistoryAvailable: boolean = !!props.invoice?.kpiHistoryList?.length

  /**
   * Return the task list of a KPI instance, sorted alphabetically.
   */
  function sortKpiTaskList(kpi: Kpi) {
    return Object.values(kpi.taskDetailList).sort((a, b) =>
      a.moduleName.localeCompare(b.moduleName)
    )
  }

  // Debug log
  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'selectedKpiIndex:', selectedKpiIndex)
  }, [selectedKpiIndex])

  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'selectedKpi:', selectedKpi)
  }, [selectedKpi])

  useEffect(() => {
    const defaultExportButtonLabel = `${DefaultPeriodExportButtonLabel} export`

    if (selectedKpi) {
      if (AccountUtil.isTierEnterprise(props.invoice?.tier)) {
        // Enterprise account with selected KPI
        const periodName = getPeriodName(selectedKpi, props.invoice?.tier)
        setPeriodExportButtonLabel(`${periodName} export`)
      } else {
        // Other account with selected KPI
        setPeriodExportButtonLabel(defaultExportButtonLabel)
      }
    } else {
      // Undefined account or KPI
      setPeriodExportButtonLabel(defaultExportButtonLabel)
    }
  }, [selectedKpi])

  /**
   * Handle the change of the selection of the period.
   */
  const handleKpiSelectionChange = (event: SelectChangeEvent<number>) => {
    const index = Number(event.target.value)

    if (props.invoice && index < props.invoice?.kpiHistoryList.length) {
      setSelectedKpiIndex(index)
      setSelectedKpi(props.invoice.kpiHistoryList[index])
    } else {
      setSelectedKpiIndex(undefined)
      setSelectedKpi(undefined)
    }
  }

  /**
   * Return the name of the KPI period.
   *
   * @param kpi The Kpi instance.
   * @param tierType The type of tier.
   * @returns The name in string.
   */
  function getPeriodName(kpi: Kpi, tierType?: TierType) {
    if (AccountUtil.isTierEnterprise(tierType)) {
      return kpi.periodStart.toLocaleDateString(Util.getLocale(), {
        year: 'numeric',
        month: 'long',
      })
    }

    return kpi.periodStart.toLocaleDateString(Util.getLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  /**
   * The component of the description of the Usage tab.
   */
  const Description = () => {
    if (selectedKpi) {
      if (AccountUtil.isTierEnterprise(props.invoice?.tier)) {
        // Enterprise subscription
        return (
          <div className="description">
            Track your usage for{' '}
            <span className="bold">
              the period of {getPeriodName(selectedKpi, props.invoice?.tier)}
            </span>
            .
          </div>
        )
      } else {
        // Other subscriptions
        return (
          <div className="description">
            Track your usage for the period{' '}
            <span className="bold">
              from {TextUtil.dateToMonthDayWithOrdinal(selectedKpi.periodStart)}{' '}
              to {TextUtil.dateToMonthDayWithOrdinal(selectedKpi.periodEnd)}
            </span>
            .
          </div>
        )
      }
    } else {
      // Unknow period
      return (
        <div className="description">
          Track your usage for <span className="bold">the selected period</span>
          .
        </div>
      )
    }
  }

  const ValueComp = ({
    label,
    formattedValue,
  }: {
    label: string
    formattedValue: string
  }) => {
    return (
      <div className="usage-value-container">
        <div className="label">{label}</div>
        <div className="value">{formattedValue}</div>
      </div>
    )
  }

  interface PeriodProps {
    invoice?: PlanInvoice
    selectedIndex: number | undefined
    onChange: (event: SelectChangeEvent<number>) => void
    disabled?: boolean
  }

  /**
   * Customized Select component for the selection of the KPI period.
   */
  const PeriodSelect = (props: PeriodProps) => {
    function getIndex(
      history: Kpi[] | undefined,
      requestedIndex: number | undefined
    ) {
      if (
        !history ||
        undefined === requestedIndex ||
        history.length < requestedIndex
      ) {
        // MUi default value is ''
        return ''
      }

      return requestedIndex
    }

    return (
      <Select
        className="date-selector"
        labelId="kpi-selector-label"
        id="kpi-selector"
        value={getIndex(props.invoice?.kpiHistoryList, props.selectedIndex)}
        onChange={props.onChange}
        inputProps={{ 'aria-label': 'Without label' }}
        disabled={props.disabled}
        MenuProps={{
          // Avoid shift of body to the left when opening the drop-down menu
          // due to the add of right-padding to compensate scroll bar appearance
          disableScrollLock: true,
        }}
      >
        {props.invoice?.kpiHistoryList.map((kpi: Kpi, index: number) => (
          <MenuItem key={kpi.periodStart.toISOString()} value={index}>
            {`${getPeriodName(kpi, props.invoice?.tier)} `}
          </MenuItem>
        ))}
      </Select>
    )
  }

  /**
   * Get the default file name of the export CSV file.
   * @param kpi KPI instance of the data to export. Its start date will be used.
   * @param tier The subscription tier.
   * @returns The file name.
   */
  function getCsvFileName(kpi?: Kpi, tier?: TierType): string {
    let periodDate = ''
    if (kpi) {
      const date = kpi.periodStart
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear())

      periodDate = `${year}${month}`
      if (!AccountUtil.isTierEnterprise(tier)) {
        periodDate = `${periodDate}${day}`
      }
    } else {
      periodDate = `${new Date().getFullYear()}`
    }

    return `${CSV_FILE_NAME_PREFIX}-${periodDate}${CSV_FILE_EXT}`
  }

  /**
   * Add the data of the given KPI instance to an existing CSV data structure.
   * @param csvData
   *    The CSV data of destination.
   * @param kpi
   *    The KPI instance.
   */
  function addToCsvData(csvData: any[][], kpi: Kpi) {
    let periodString = ''
    if (AccountUtil.isTierEnterprise(props.invoice?.tier)) {
      // Enterprise subscription
      periodString = getPeriodName(kpi, props.invoice?.tier)
    } else {
      // Other subscription
      const start = TextUtil.dateToMonthDayWithOrdinal(kpi.periodStart)
      const startYear = kpi.periodStart.getFullYear()
      const end = TextUtil.dateToMonthDayWithOrdinal(kpi.periodEnd)
      const endYear = kpi.periodStart.getFullYear()
      if (startYear === endYear) {
        periodString = `${start} to ${end} ${startYear}`
      } else {
        periodString = `${start} ${startYear} to ${end} ${endYear}`
      }
    }

    for (const task of sortKpiTaskList(kpi)) {
      // Add a new row of data
      csvData.push([
        periodString,
        task.moduleName,
        task.trackProcessedCount,
        Util.absRounding(task.consumedCreditCount),
        Util.absRounding(task.consumedOffPackageCreditCount),
      ])
    }
  }

  /**
   * Export the usage data to a CSV file.
   *
   * @param kpi
   *    The KPI instance which data will be exported. If undefined, the data
   *    of all the KPI instances the current year are exported.
   * @param filename
   *    The file name of the CSV file. If undefined, the default one is used.
   */
  function exportKpiToCsv(kpi?: Kpi, filename?: string) {
    const csvData: any[][] = [
      ['Period', ...HeaderList.map((header: ColumnHeader) => header.title)],
    ]

    if (kpi) {
      // Get data for that KPI
      addToCsvData(csvData, kpi)
    } else if (props.invoice) {
      // Get data for all the KPI of the current year
      const thisYear = new Date().getFullYear()
      for (const itKpi of props.invoice.kpiHistoryList) {
        if (thisYear === itKpi.periodStart.getFullYear()) {
          addToCsvData(csvData, itKpi)
        }
      }
    }

    // Write CSV file
    const localFileName = filename ?? getCsvFileName(kpi, props.invoice?.tier)
    writeCsv(localFileName, csvData)
  }

  return (
    <div className={fullClassName}>
      <div className="usage-table-header">
        <div className="title-description">
          <div className="billing-component-title">Usage Table</div>
          <Description />
        </div>

        <div className="export-button-container">
          <Button
            label={periodExportButtonLabel}
            className={'cursor-pointer'}
            dark
            onClick={() => exportKpiToCsv(selectedKpi)}
          />

          <Button
            label="Yearly export"
            className={'cursor-pointer'}
            dark
            onClick={() => exportKpiToCsv()}
          />
        </div>
      </div>

      <div className="total-date-picker-container">
        <div className="total-container">
          {TierType.TopUpCreditAimd != props.invoice?.tier && (
            <ValueComp
              label="Estimated total"
              formattedValue={TextUtil.formatPrice(
                props.invoice?.totalCost ?? 0,
                props.invoice?.currency ?? '€',
                true
              )}
            />
          )}

          <ValueComp
            label="Number of processed tracks"
            formattedValue={`${selectedKpi?.allTrackProcessedCount ?? 0}`}
          />
        </div>

        <PeriodSelect
          invoice={props.invoice}
          selectedIndex={selectedKpiIndex}
          onChange={handleKpiSelectionChange}
          disabled={!isKpiHistoryAvailable}
        />
      </div>

      <TaskTable taskDetailList={selectedKpi?.taskDetailList ?? {}}></TaskTable>
    </div>
  )
}

export default UsageTab
